/**
 * The page's end of `/api/assist/listen`: the microphone in, 16 kHz frames
 * out, and `ListenEvent`s back. Dictation and voice mode both open one.
 *
 * The socket is refused by an event, never by its handshake — a browser
 * cannot read why a handshake failed — so every refusal arrives as `error`
 * followed by `end`.
 */

import type { ListenEvent, ListenMode } from '@wire';
import { Framer, level, Resampler, toInt16 } from './pcm';

export interface ListenHandlers {
  onEvent(e: ListenEvent): void;
  /** The microphone's loudness, 0 to 1, about twelve times a second. */
  onLevel?(level: number): void;
  /** The socket is gone, for whatever reason; `error` has already said why
   *  if there was one. */
  onClosed(): void;
}

export interface Listening {
  /** Muted audio still flows, as silence, so a turn can end. */
  setMuted(muted: boolean): void;
  /** No more audio: the model gets to say its last words, then it closes. */
  finish(): void;
  /** Closes at once. */
  close(): void;
}

/** Whether this browser can listen at all. */
export const canListen = (): boolean =>
  typeof window !== 'undefined' &&
  typeof WebSocket !== 'undefined' &&
  typeof AudioWorkletNode !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia;

const socketUrl = (mode: ListenMode) => {
  const u = new URL('/api/assist/listen', location.href);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.searchParams.set('mode', mode);
  return u.toString();
};

/**
 * Opens the microphone and the socket together. Rejects when either cannot
 * open (no permission, no microphone); after that, trouble comes as events.
 * `context` may be one unlocked by the click that started this, so voice
 * mode can play into it too.
 */
export async function listen(mode: ListenMode, handlers: ListenHandlers, context?: AudioContext): Promise<Listening> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 }
  });
  const ctx = context ?? new AudioContext();
  const ownContext = !context;
  let ws: WebSocket | null = null;
  let node: AudioWorkletNode | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let muted = false;
  let closed = false;

  const teardown = () => {
    if (closed) return;
    closed = true;
    node?.port.close();
    node?.disconnect();
    source?.disconnect();
    for (const track of stream.getTracks()) track.stop();
    if (ownContext) void ctx.close().catch(() => undefined);
    handlers.onClosed();
  };

  try {
    await ctx.audioWorklet.addModule('/pcm-capture.js');
    if (ctx.state === 'suspended') await ctx.resume();
    source = ctx.createMediaStreamSource(stream);
    node = new AudioWorkletNode(ctx, 'pcm-capture');
    source.connect(node);
    // Into the graph, or the audio thread need not run it. It writes nothing
    // to its output, so nothing is heard.
    node.connect(ctx.destination);
  } catch (err) {
    for (const track of stream.getTracks()) track.stop();
    if (ownContext) void ctx.close().catch(() => undefined);
    throw err;
  }

  const resampler = new Resampler(ctx.sampleRate);
  const framer = new Framer();
  const pending: ArrayBuffer[] = [];

  ws = new WebSocket(socketUrl(mode));
  ws.binaryType = 'arraybuffer';
  ws.onmessage = (e) => {
    if (typeof e.data !== 'string') return;
    try {
      handlers.onEvent(JSON.parse(e.data) as ListenEvent);
    } catch {
      /* not ours */
    }
  };
  ws.onopen = () => {
    for (const frame of pending.splice(0)) ws?.send(frame);
  };
  ws.onclose = teardown;
  ws.onerror = () => ws?.close();

  node.port.onmessage = (e: MessageEvent<Float32Array>) => {
    if (closed) return;
    const chunk = muted ? new Float32Array(e.data.length) : e.data;
    handlers.onLevel?.(muted ? 0 : level(e.data));
    for (const frame of framer.push(toInt16(resampler.push(chunk)))) {
      if (ws?.readyState === WebSocket.OPEN) ws.send(frame);
      // Speech from before the socket opened is kept, a second of it at most.
      else if (pending.length < 10) pending.push(frame);
    }
  };

  return {
    setMuted: (m) => {
      muted = m;
    },
    finish: () => {
      if (closed || !ws) return;
      const last = framer.flush();
      if (last && ws.readyState === WebSocket.OPEN) ws.send(last);
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'finish' }));
      // Stop hearing now; the socket closes when the model has answered.
      node?.disconnect();
      source?.disconnect();
      for (const track of stream.getTracks()) track.stop();
      setTimeout(() => ws?.close(), 3_000);
    },
    close: () => {
      ws?.close();
      teardown();
    }
  };
}
